import type { PlainAgentExecution } from "@/queries/agent-queries";

// Subagent name prefix constant - matches backend/mcp/subagent/server.go
export const SUBAGENT_NAME_PREFIX = "[subagent:";

// Attribution prefix for messages from subagents
export const SUBAGENT_ATTRIBUTION_PREFIX = "[from:subagent:";

// Regex to parse subagent name: [subagent:<parent_id>] <task>
const SUBAGENT_NAME_REGEX = /^\[subagent:([a-fA-F0-9-]+)\]\s*(.*)$/;

// Regex to parse subagent attribution: [from:subagent:<id>]\n<message>
const SUBAGENT_ATTRIBUTION_REGEX = /^\[from:subagent:([a-fA-F0-9-]+)\]\n([\s\S]*)$/;

/**
 * Check if an agent execution is a subagent based on its name.
 */
export function isSubagentExecution(execution: PlainAgentExecution): boolean {
    return execution.metadata?.name?.startsWith(SUBAGENT_NAME_PREFIX) ?? false;
}

/**
 * Parse subagent name to extract parent execution ID and task description.
 * Returns null if the name doesn't match the subagent format.
 */
export function parseSubagentName(name: string): { parentId: string; task: string } | null {
    const match = SUBAGENT_NAME_REGEX.exec(name);
    if (!match) return null;
    return { parentId: match[1], task: match[2] };
}

/**
 * Parse subagent attribution from message content.
 * Returns the execution ID and the actual message content without the attribution prefix.
 */
export function parseSubagentAttribution(content: string): {
    executionId: string;
    messageContent: string;
} | null {
    const match = SUBAGENT_ATTRIBUTION_REGEX.exec(content);
    if (!match) return null;
    return { executionId: match[1], messageContent: match[2] };
}

/**
 * Get the task name from a subagent execution.
 * Returns the task portion of the subagent name, or undefined if not a subagent.
 */
export function getSubagentTask(execution: PlainAgentExecution): string | undefined {
    const name = execution.metadata?.name;
    if (!name) return undefined;
    const parsed = parseSubagentName(name);
    return parsed?.task;
}
